import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import TopBar, { Logo } from "@/components/journey/TopBar";
import StepProgress from "@/components/journey/StepProgress";
import StepHero from "@/components/journey/StepHero";
import StepWelcome from "@/components/journey/StepWelcome";
import StepDetails from "@/components/journey/StepDetails";
import StepBooking from "@/components/journey/StepBooking";
import StepConfirm from "@/components/journey/StepConfirm";
import Tm from "@/components/journey/Tm";

const STEPS = { 0: StepHero, 1: StepWelcome, 2: StepDetails, 3: StepBooking, 4: StepConfirm };

function useCashfreeReturn() {
  const { state, update, t } = useApp();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("cf_order")) return;
    window.history.replaceState({}, "", window.location.pathname);
    const b = state.booking;
    if (!b) return;
    toast.message(t("verifying"));
    api
      .post(`/bookings/${b.id}/cashfree-verify`, { token: b.token })
      .then(({ data }) => {
        if (["paid", "payment_submitted", "confirmed", "completed"].includes(data.status)) {
          update({ booking: { ...b, ...data }, step: 4 });
        } else {
          update({ step: 3 });
          toast.error(t("notPaid"));
        }
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function Journey() {
  const { state, settings, lang, t } = useApp();
  const soundRef = useRef(null);
  useCashfreeReturn();
  const Step = STEPS[state.step] ?? StepHero;

  return (
    <div className={`relative z-10 flex min-h-screen flex-col ${lang === "ml" ? "lang-ml" : ""}`}>
      <TopBar soundRef={soundRef} />
      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-2 sm:items-center sm:px-8">
        <div className="w-full max-w-3xl">
          {state.lang && state.step > 1 && settings && (
            <div className="mb-5 flex items-center justify-center gap-3" data-testid="step-brand-header">
              <Logo size="h-14 w-14" />
              <div>
                <p className="font-display text-2xl font-semibold tracking-[0.12em] text-white">JOYFUL MINDS<Tm sizeEm={0.55} /></p>
                <p className="text-xs text-amber-200/90">{t("brandSub")}</p>
              </div>
            </div>
          )}
          {state.lang && state.step > 1 && <StepProgress />}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${state.step}-${!!state.lang}`}
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            >
              {settings ? <Step soundRef={soundRef} /> : <div className="glass h-64 animate-pulse rounded-3xl" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
